/**
 * VocabularyModal Component
 * Modal popup showing comprehensive vocabulary information including:
 * - Pronunciation (IPA) from dictionary API
 * - Audio playback from dictionary
 * - Context-specific and general definitions
 * - Example sentences
 * - Synonyms, antonyms, collocations
 * - Interactive quiz with 4 options
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ComprehensiveVocabularyEntry, VocabularyQuizOption } from '../../types/creator-workflow';
import { dictionaryService, DictionaryWord } from '../../services/dictionaryService';
import { cefrKnowledgeService, CEFRLevel } from '../../services/cefrKnowledgeService';
import './VocabularyModal.css';

import { resolveMediaUrl } from '../../utils/mediaUrl';

interface VocabularyModalProps {
  vocabulary: ComprehensiveVocabularyEntry;
  onClose: () => void;
}

const VocabularyModal: React.FC<VocabularyModalProps> = ({ vocabulary, onClose }) => {
  const { t } = useTranslation('creator');
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [dictDataList, setDictDataList] = useState<DictionaryWord[]>([]);
  const [isLoadingDict, setIsLoadingDict] = useState(true);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [cefrLevel, setCefrLevel] = useState<CEFRLevel | null>(null);
  const [isLoadingCefr, setIsLoadingCefr] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioQueueRef = useRef<string[]>([]);
  const currentAudioIndexRef = useRef<number>(0);

  // Fetch dictionary data on mount - handles multi-word phrases
  useEffect(() => {
    const fetchDictionaryData = async () => {
      setIsLoadingDict(true);
      try {
        // Split by spaces to handle multi-word vocabulary (e.g., "squeeze out")
        const words = vocabulary.word.trim().split(/\s+/);
        const results: DictionaryWord[] = [];

        for (const word of words) {
          try {
            const data = await dictionaryService.lookup(word);
            if (data) {
              results.push(data);
            }
          } catch (error) {
            console.error(`Failed to fetch dictionary data for "${word}":`, error);
            // Continue with other words even if one fails
          }
        }

        setDictDataList(results);
      } catch (error) {
        console.error('Failed to fetch dictionary data:', error);
      } finally {
        setIsLoadingDict(false);
      }
    };

    fetchDictionaryData();
  }, [vocabulary.word]);

  // Fetch CEFR level for this vocabulary word
  useEffect(() => {
    let isMounted = true;
    setCefrLevel(null); // Reset when word changes

    const fetchCefrLevel = async () => {
      try {
        const results = await cefrKnowledgeService.searchKnowledgePoints(vocabulary.word, 1);
        if (isMounted && results.length > 0 && results[0].term.toLowerCase() === vocabulary.word.toLowerCase()) {
          setCefrLevel(results[0].cefrLevel);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to fetch CEFR level:', error);
        }
      } finally {
        if (isMounted) {
          setIsLoadingCefr(false);
        }
      }
    };

    fetchCefrLevel();
    return () => { isMounted = false; };
  }, [vocabulary.word]);

  // Combine phonetics from multiple dictionary results
  const displayPhonetic = (() => {
    if (dictDataList.length > 0) {
      const phonetics = dictDataList.map(d => d.phonetic).filter(Boolean);
      if (phonetics.length > 0) {
        return phonetics.join(' ');
      }
    }
    return vocabulary.pronunciation;
  })();

  // Get all audio URLs from dictionary results
  const getAudioUrls = (): string[] => {
    const urls: string[] = [];
    for (const dictData of dictDataList) {
      if (dictData?.audio && dictData.audio.length > 0) {
        const audioUrl = resolveMediaUrl(dictData.audio[0].url);
        urls.push(audioUrl);
      }
    }
    return urls;
  };

  // Check if we have any audio to play
  const hasAudio = (): boolean => {
    return getAudioUrls().length > 0;
  };

  // Play pronunciation audio - handles multiple audio files sequentially
  const playAudio = () => {
    const audioUrls = getAudioUrls();
    if (audioUrls.length === 0) return;

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Set up audio queue
    audioQueueRef.current = audioUrls;
    currentAudioIndexRef.current = 0;
    setIsPlayingAudio(true);

    // Play first audio
    playNextAudio();
  };

  // Play next audio in queue
  const playNextAudio = () => {
    const audioUrls = audioQueueRef.current;
    const currentIndex = currentAudioIndexRef.current;

    if (currentIndex >= audioUrls.length) {
      // All audio played
      setIsPlayingAudio(false);
      return;
    }

    const audioUrl = audioUrls[currentIndex];
    audioRef.current = new Audio(audioUrl);

    audioRef.current.onended = () => {
      currentAudioIndexRef.current += 1;
      // Small delay between words
      setTimeout(() => playNextAudio(), 200);
    };

    audioRef.current.onerror = () => {
      console.error('Failed to play audio:', audioUrl);
      currentAudioIndexRef.current += 1;
      playNextAudio();
    };

    audioRef.current.play().catch(err => {
      console.error('Failed to play audio:', err);
      currentAudioIndexRef.current += 1;
      playNextAudio();
    });
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleAnswerSelect = (option: VocabularyQuizOption) => {
    setSelectedAnswer(option.text);
    setShowExplanation(true);
  };

  const isCorrectAnswer = (option: VocabularyQuizOption): boolean => {
    return option.is_correct;
  };

  const getOptionClassName = (option: VocabularyQuizOption): string => {
    if (!showExplanation) return 'quiz-option';

    if (option.is_correct) return 'quiz-option correct';
    if (selectedAnswer === option.text && !option.is_correct) return 'quiz-option incorrect';
    return 'quiz-option';
  };

  const getDifficultyColor = (difficulty: number): string => {
    if (difficulty <= 3) return 'easy';
    if (difficulty <= 6) return 'medium';
    return 'hard';
  };

  const getFrequencyLabel = (frequency: string): string => {
    const keyMap: Record<string, string> = {
      common: 'workflow.vocabularyModal.frequencyCommon',
      uncommon: 'workflow.vocabularyModal.frequencyUncommon',
      rare: 'workflow.vocabularyModal.frequencyRare'
    };
    return keyMap[frequency] ? t(keyMap[frequency]) : frequency;
  };

  const getPosLabel = (pos: string): string => {
    const keyMap: Record<string, string> = {
      noun: 'workflow.vocabularyModal.posNoun',
      verb: 'workflow.vocabularyModal.posVerb',
      adjective: 'workflow.vocabularyModal.posAdjective',
      adverb: 'workflow.vocabularyModal.posAdverb',
      other: 'workflow.vocabularyModal.posOther'
    };
    return keyMap[pos] ? t(keyMap[pos]) : pos;
  };

  return createPortal(
    <div className="vocabulary-modal-overlay" onClick={onClose}>
      <div className="vocabulary-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="word-title">
            <h2>{vocabulary.word}</h2>
            <div className="pronunciation-container">
              <span className="pronunciation">
                {isLoadingDict ? '...' : `/${displayPhonetic}/`}
              </span>
              {hasAudio() && (
                <button
                  className={`audio-button ${isPlayingAudio ? 'playing' : ''}`}
                  onClick={playAudio}
                  disabled={isPlayingAudio}
                  title={t('workflow.vocabularyModal.playPronunciation')}
                >
                  {isPlayingAudio ? '■' : '▶'}
                </button>
              )}
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Metadata tags */}
        <div className="word-metadata">
          <span className="pos-tag">{getPosLabel(vocabulary.pos)}</span>
          {isLoadingCefr ? (
            <span className="cefr-badge cefr-badge--loading">...</span>
          ) : cefrLevel ? (
            <span className={`cefr-badge cefr-badge--${cefrLevel}`}>
              CEFR {cefrLevel}
            </span>
          ) : null}
          <span className="frequency-tag">{getFrequencyLabel(vocabulary.frequency)}</span>
        </div>

        {/* Modal content */}
        <div className="modal-content">
          {/* Definitions */}
          <section className="definition-section">
            <h3>{t('workflow.vocabularyModal.definitions')}</h3>

            <div className="definition-item context-definition">
              <h4>{t('workflow.vocabularyModal.contextMeaning')}</h4>
              <p>{vocabulary.definition_in_context}</p>
            </div>

            {vocabulary.context && (
              <div className="context-example">
                <h4>{t('workflow.vocabularyModal.contextInText')}</h4>
                <p className="context-text">{vocabulary.context}</p>
              </div>
            )}

            {(vocabulary.general_definition_en || vocabulary.general_definition) && (
              <div className="definition-item general-definition">
                <h4>{t('workflow.vocabularyModal.generalDefinition')}</h4>
                {vocabulary.general_definition_en && <p className="def-en">{vocabulary.general_definition_en}</p>}
                {vocabulary.general_definition && <p className="def-zh">{vocabulary.general_definition}</p>}
              </div>
            )}
          </section>

          {/* Example sentences */}
          {vocabulary.example_sentences && vocabulary.example_sentences.length > 0 && (
            <section className="examples-section">
              <h3>{t('workflow.vocabularyModal.exampleSentences')}</h3>
              {vocabulary.example_sentences.map((example, idx) => (
                <div key={idx} className="example-item">
                  <p className="example-en">{example.sentence_en}</p>
                  <p className="example-zh">{example.sentence_native}</p>
                </div>
              ))}
            </section>
          )}

          {/* Related words */}
          <section className="related-words-section">
            {vocabulary.synonyms && vocabulary.synonyms.length > 0 && (
              <div className="related-group">
                <h4>{t('workflow.vocabularyModal.synonyms')}</h4>
                <div className="word-tags">
                  {vocabulary.synonyms.map((word, idx) => (
                    <span key={idx} className="word-tag synonym">{word}</span>
                  ))}
                </div>
              </div>
            )}

            {vocabulary.antonyms && vocabulary.antonyms.length > 0 && (
              <div className="related-group">
                <h4>{t('workflow.vocabularyModal.antonyms')}</h4>
                <div className="word-tags">
                  {vocabulary.antonyms.map((word, idx) => (
                    <span key={idx} className="word-tag antonym">{word}</span>
                  ))}
                </div>
              </div>
            )}

            {vocabulary.collocations && vocabulary.collocations.length > 0 && (
              <div className="related-group">
                <h4>{t('workflow.vocabularyModal.collocations')}</h4>
                <div className="word-tags">
                  {vocabulary.collocations.map((phrase, idx) => (
                    <span key={idx} className="word-tag collocation">{phrase}</span>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Vocabulary Quiz */}
          {vocabulary.vocabulary_quiz && (
            <section className="quiz-section">
              <h3>{t('workflow.vocabularyModal.quizTitle')}</h3>

              <div className="quiz-question">
                <p className="question-en">{vocabulary.vocabulary_quiz.question_en}</p>
                <p className="question-zh">{vocabulary.vocabulary_quiz.question_native}</p>
              </div>

              <div className="quiz-options">
                {vocabulary.vocabulary_quiz.options.map((option, idx) => (
                  <button
                    key={idx}
                    className={getOptionClassName(option)}
                    onClick={() => handleAnswerSelect(option)}
                    disabled={showExplanation}
                  >
                    <span className="option-letter">{String.fromCharCode(65 + idx)}</span>
                    <div className="option-content">
                      <span className="option-text">{option.text}</span>
                      <span className="option-translation">{option.translation}</span>
                    </div>
                    {showExplanation && isCorrectAnswer(option) && (
                      <span className="check-icon">✓</span>
                    )}
                    {showExplanation && selectedAnswer === option.text && !option.is_correct && (
                      <span className="cross-icon">✗</span>
                    )}
                  </button>
                ))}
              </div>

              {showExplanation && (
                <div className="quiz-explanation">
                  <h4>{t('workflow.vocabularyModal.explanation')}</h4>
                  <p className="explanation-en">{vocabulary.vocabulary_quiz.explanation_en}</p>
                  <p className="explanation-zh">{vocabulary.vocabulary_quiz.explanation_native}</p>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VocabularyModal;
